import * as React from "react";
import { UitkSecondaryButton } from "uitk-react-button";
import { useLocalization } from "@shared-ui/localization-context";
import { UitkCardTextSegment } from "uitk-react-cards";
import { FlexClickTracker } from "components/utility/analytics/FlexClickTracker/FlexClickTracker";
import { ExtendedContextStore } from "src/typings/flexFramework/FlexDefinitions";
import { HotelSearchLinkBuilder } from "components/utility/HotelSearchLinkBuilder/HotelSearchLinkBuilder";
import { isVariantEnabled, Experiment } from "components/utility/experiment";

interface SeeAllButtonProps {
  showMoreStrategy: string;
  seeAllLink: string;
  seeAllType: string;
  moduleName: string;
  locationName: string;
  isWeDontHaveAllInclusiveExperimentEnabled?: boolean;
  affinityDescription?: string;
  affinityCode?: string;
  context: ExtendedContextStore;
  moduleView?: string;
  totalHotels?: number;
}

interface ButtonTrackedProps {
  ["data-testid"]: string;
  moduleName: string;
  rfrr: string;
  text: string;
  rel: string | undefined;
  href: string | undefined;
  onClick?: () => void;
  linkName: string;
}
const ButtonTracked = FlexClickTracker(UitkSecondaryButton);

export const SeeAllButton = (props: SeeAllButtonProps) => {
  const { locationName, affinityDescription, affinityCode, moduleView, totalHotels } = props;
  const { formatText } = useLocalization();

  if (props.showMoreStrategy !== "button" && props.showMoreStrategy !== "affinityMessage") {
    return null;
  }

  /* istanbul ignore next */
  const scrollTo = (): void => {
    const wizardElement = document.getElementById("wizard-hotel-pwa-1");

    if (wizardElement) {
      wizardElement.scrollIntoView({
        block: "start",
        behavior: "smooth",
      });
      wizardElement.focus();
    }
  };

  let weGotMoreMessage;
  let btnTrackedProps: ButtonTrackedProps = {
    ["data-testid"]: "hotels-cta-button",
    moduleName: props.moduleName,
    rfrr: props.seeAllType,
    text: formatText("hotels.viewAllProperties"),
    rel: "nofollow",
    href: buildLinkToSRP(props.context) || props.seeAllLink,
    linkName: props.seeAllLink,
  };

  if (moduleView === "affinity") {
    weGotMoreMessage = formatText(`hotels.${affinityCode}.gotMoreProperties`, totalHotels);
    btnTrackedProps = {
      ...btnTrackedProps,
      text: formatText(`hotels.${affinityCode}.seeAllAvailableProperties`),
      rel: undefined,
      href: undefined,
      onClick: scrollTo,
      linkName: "Call to action click event in hotels module",
    };
  } else if (affinityDescription) {
    weGotMoreMessage = formatText("hotels.affinity.gotMoreProperties", affinityDescription, locationName);
  } else {
    weGotMoreMessage = formatText("hotels.seeAllAvailableProperties", locationName);
  }

  return (
    <UitkCardTextSegment className="uitk-type-center">
      <div className="uitk-type-bold all-r-margin-one hotelRating uitk-type-300 all-t-padding-three">
        {formatText("hotels.lookingFor")}
      </div>
      {weGotMoreMessage && <div className="uitk-type-300">{weGotMoreMessage}</div>}

      <UitkCardTextSegment>
        <ButtonTracked {...btnTrackedProps} />
      </UitkCardTextSegment>
    </UitkCardTextSegment>
  );
};

/**
 * Manually builds a link to SRP with +30 days ahead and respective filters
 * Only available for SRP Lite Experiment.
 *
 * @param context
 */
const buildLinkToSRP = (context: ExtendedContextStore) => {
  if (
    !isVariantEnabled(context, Experiment.Blossom_SRP_Lite_Desktop) &&
    !isVariantEnabled(context, Experiment.Blossom_SRP_Lite_Mobile)
  ) {
    return null;
  }

  const link = new HotelSearchLinkBuilder()
    .withDaysInFuture()
    .withStayLength()
    .withRegionId(context.searchContext?.location?.id);

  // Add filters to SRP link
  const filters = context.searchContext?.affinity?.hsrFilters;
  if (filters) {
    Object.keys(filters).forEach((key) => {
      link.withFilter(key, filters[key]);
    });
  }

  return link.build();
};
